import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { sensorsApi } from '../../services/api';
import { XMarkIcon } from '@heroicons/react/24/outline';

// ============================================================================
// Validation schema
// ============================================================================

const configureSensorSchema = z.object({
  name: z.string().min(1, 'Sensor name is required').max(100),
  status: z.enum(['ONLINE', 'OFFLINE', 'MAINTENANCE']),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  altitudeMSL: z.string().optional(),
  updateRateHz: z.string().optional(),
  maxRange: z.string().optional(),
  minRange: z.string().optional(),
  fieldOfView: z.string().optional(),
});

type ConfigureSensorForm = z.infer<typeof configureSensorSchema>;

// ============================================================================
// Component
// ============================================================================

interface ConfigureSensorModalProps {
  isOpen: boolean;
  onClose: () => void;
  sensor: any;
}

export default function ConfigureSensorModal({ isOpen, onClose, sensor }: ConfigureSensorModalProps) {
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConfigureSensorForm>({
    resolver: zodResolver(configureSensorSchema),
    defaultValues: {
      name: sensor?.name || '',
      status: sensor?.status || 'OFFLINE',
      latitude: sensor?.latitude?.toString() || '',
      longitude: sensor?.longitude?.toString() || '',
      altitudeMSL: sensor?.altitudeMsl?.toString() || '',
      updateRateHz: sensor?.configuration?.updateRateHz?.toString() || '',
      maxRange: sensor?.configuration?.maxRange?.toString() || '',
      minRange: sensor?.configuration?.minRange?.toString() || '',
      fieldOfView: sensor?.configuration?.fieldOfView?.toString() || '',
    },
  });

  // Reset form when sensor changes
  useEffect(() => {
    if (sensor) {
      reset({
        name: sensor.name || '',
        status: sensor.status || 'OFFLINE',
        latitude: sensor.latitude?.toString() || '',
        longitude: sensor.longitude?.toString() || '',
        altitudeMSL: sensor.altitudeMsl?.toString() || '',
        updateRateHz: sensor.configuration?.updateRateHz?.toString() || '',
        maxRange: sensor.configuration?.maxRange?.toString() || '',
        minRange: sensor.configuration?.minRange?.toString() || '',
        fieldOfView: sensor.configuration?.fieldOfView?.toString() || '',
      });
    }
  }, [sensor, reset]);

  const onSubmit = async (data: ConfigureSensorForm) => {
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name: data.name,
        status: data.status,
      };

      // Parse location
      const lat = data.latitude ? parseFloat(data.latitude) : undefined;
      const lon = data.longitude ? parseFloat(data.longitude) : undefined;
      const alt = data.altitudeMSL ? parseFloat(data.altitudeMSL) : undefined;

      if (lat !== undefined && !isNaN(lat) && lon !== undefined && !isNaN(lon)) {
        if (lat < -90 || lat > 90) {
          toast.error('Latitude must be between -90 and 90');
          setSubmitting(false);
          return;
        }
        if (lon < -180 || lon > 180) {
          toast.error('Longitude must be between -180 and 180');
          setSubmitting(false);
          return;
        }
        payload.location = {
          latitude: lat,
          longitude: lon,
          ...(alt !== undefined && !isNaN(alt) ? { altitudeMSL: alt } : {}),
        };
      }

      // Parse configuration
      const config: Record<string, number> = {};
      const updateRate = data.updateRateHz ? parseFloat(data.updateRateHz) : undefined;
      const maxRange = data.maxRange ? parseFloat(data.maxRange) : undefined;
      const minRange = data.minRange ? parseFloat(data.minRange) : undefined;
      const fov = data.fieldOfView ? parseFloat(data.fieldOfView) : undefined;

      if (updateRate !== undefined && !isNaN(updateRate)) config.updateRateHz = updateRate;
      if (maxRange !== undefined && !isNaN(maxRange)) config.maxRange = maxRange;
      if (minRange !== undefined && !isNaN(minRange)) config.minRange = minRange;
      if (fov !== undefined && !isNaN(fov)) config.fieldOfView = fov;

      if (Object.keys(config).length > 0) {
        payload.configuration = config;
      }

      await sensorsApi.update(sensor.id, payload);
      toast.success('Sensor updated');
      queryClient.invalidateQueries({ queryKey: ['sensor', sensor.id] });
      queryClient.invalidateQueries({ queryKey: ['sensors'] });
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update sensor';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const inputClasses =
    'w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent text-sm';
  const selectClasses =
    'w-full px-3 py-2 bg-navy-800 border border-navy-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent text-sm';
  const labelClasses = 'block text-xs font-medium text-gray-400 mb-1.5';
  const errorClasses = 'text-alert text-xs mt-1';

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={handleClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-lg rounded-xl bg-navy-800 border border-navy-700 p-6 shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-lg font-semibold text-white">
                    Configure Sensor
                  </Dialog.Title>
                  <button onClick={handleClose}
                    className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-navy-700 transition-colors">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                  {/* Name + Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="cfg-name" className={labelClasses}>Name</label>
                      <input id="cfg-name" type="text" className={inputClasses} {...register('name')} />
                      {errors.name && <p className={errorClasses}>{errors.name.message}</p>}
                    </div>
                    <div>
                      <label htmlFor="cfg-status" className={labelClasses}>Status</label>
                      <select id="cfg-status" className={selectClasses} {...register('status')}>
                        <option value="ONLINE">Online</option>
                        <option value="OFFLINE">Offline</option>
                        <option value="MAINTENANCE">Maintenance</option>
                      </select>
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <p className="text-xs font-medium text-gray-400 mb-2">Location</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label htmlFor="cfg-lat" className="block text-[10px] text-gray-500 mb-1">Latitude</label>
                        <input id="cfg-lat" type="number" step="any" className={inputClasses} {...register('latitude')} />
                      </div>
                      <div>
                        <label htmlFor="cfg-lon" className="block text-[10px] text-gray-500 mb-1">Longitude</label>
                        <input id="cfg-lon" type="number" step="any" className={inputClasses} {...register('longitude')} />
                      </div>
                      <div>
                        <label htmlFor="cfg-alt" className="block text-[10px] text-gray-500 mb-1">Alt (m MSL)</label>
                        <input id="cfg-alt" type="number" step="any" className={inputClasses} {...register('altitudeMSL')} />
                      </div>
                    </div>
                  </div>

                  {/* Configuration */}
                  <div>
                    <p className="text-xs font-medium text-gray-400 mb-2">Configuration</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="cfg-rate" className="block text-[10px] text-gray-500 mb-1">Update Rate (Hz)</label>
                        <input id="cfg-rate" type="number" step="any" className={inputClasses} {...register('updateRateHz')} />
                      </div>
                      <div>
                        <label htmlFor="cfg-max-range" className="block text-[10px] text-gray-500 mb-1">Max Range (m)</label>
                        <input id="cfg-max-range" type="number" step="any" className={inputClasses} {...register('maxRange')} />
                      </div>
                      <div>
                        <label htmlFor="cfg-min-range" className="block text-[10px] text-gray-500 mb-1">Min Range (m)</label>
                        <input id="cfg-min-range" type="number" step="any" className={inputClasses} {...register('minRange')} />
                      </div>
                      <div>
                        <label htmlFor="cfg-fov" className="block text-[10px] text-gray-500 mb-1">Field of View (&deg;)</label>
                        <input id="cfg-fov" type="number" step="any" className={inputClasses} {...register('fieldOfView')} />
                      </div>
                    </div>
                  </div>

                  {/* Firmware (read-only) */}
                  {sensor?.firmwareVersion && (
                    <div>
                      <label className={labelClasses}>Firmware Version</label>
                      <p className="text-sm text-gray-400 font-mono">{sensor.firmwareVersion}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-2 border-t border-navy-700/50">
                    <button type="button" onClick={handleClose} className="btn-secondary" disabled={submitting}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={submitting}>
                      {submitting ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
